/** @jsxImportSource theme-ui */
import React from 'react';
import { List } from 'semantic-ui-react';

import * as dto from 'client/dto';

import { BdParagraph, SectionHeader } from 'components/_common/SectionHeader';
import { Typography } from 'components/_common/Typography';
import { BdTooltip } from 'components/_controls/BdTooltip';
import { NoSymptomaticPeriodText } from 'components/_static/StaticTexts';

const DiseaseAttributes: React.FC<dto.DiseaseInformationModel> = ({
  diseaseOverview,
  agents,
  agentTypes,
  transmissionModes,
  acquisitionModeGroups,
  incubationPeriod,
  symptomaticPeriod,
  preventionMeasure,
  biosecurityRisk,
}) => {
  const popupBiosecurity = (
    <>
      <BdParagraph>
        <Typography variant="subtitle2" color="stone10">
          Category A
        </Typography>
        <Typography variant="caption" color="stone10">
          High mortality rate, easily disseminated or transmitted from person to person.
        </Typography>
      </BdParagraph>
      <BdParagraph>
        <Typography variant="subtitle2" color="stone10">
          Category B
        </Typography>
        <Typography variant="caption" color="stone10">
          Moderate morbidity and low mortality, moderately easy to disseminate.
        </Typography>
      </BdParagraph>
      <BdParagraph>
        <Typography variant="subtitle2" color="stone10">
          Category C
        </Typography>
        <Typography variant="caption" color="stone10">
          Emerging agents that could be engineered for mass dissemination in the future.
        </Typography>
      </BdParagraph>
    </>
  );
  return (
    <div sx={{ m: '16px' }}>
      {(diseaseOverview || []).map(({ header, body }) => (
        <React.Fragment key={header}>
          <SectionHeader>{header}</SectionHeader>
          <Typography variant="body2" color="stone90" marginBottom="16px">
            {body}
          </Typography>
        </React.Fragment>
      ))}

      <SectionHeader>Additional details</SectionHeader>
      <List className="xunpadded dont-pad-first">
        <List.Item>
          <Typography variant="body2" color="deepSea50">
            <BdTooltip text="The name of the disease/syndrome.">Pathogen/Agent</BdTooltip>
          </Typography>
          <Typography variant="body2" color="stone90">
            {agents || '—'}
          </Typography>
        </List.Item>

        <List.Item>
          <Typography variant="body2" color="deepSea50">
            <BdTooltip text="The 6 main types of agents are: protozoa, bacteria, viruses, prions, parasitic worms, and fungi.">
              Pathogen/Agent Type
            </BdTooltip>
          </Typography>
          <Typography variant="body2" color="stone90">
            {agentTypes || '—'}
          </Typography>
        </List.Item>

        <List.Item>
          <Typography variant="body2" color="deepSea50">
            <BdTooltip text="The mode of agent spread. Transmission can be direct or indirect.">
              Mode of Acquisition
            </BdTooltip>
          </Typography>
          {acquisitionModeGroups?.length > 0
            ? acquisitionModeGroups.map(({ rankName, acquisitionModes }) => (
                <React.Fragment key={rankName}>
                  <Typography variant="body2" color="stone50">
                    {rankName}
                  </Typography>
                  <ul sx={{ paddingInlineStart: '28px', mt: 0, mb: '8px' }}>
                    {acquisitionModes.map(({ label, description }) => (
                      <li key={`${rankName}-${label}`}>
                        <Typography variant="body2" color="stone90" inline>
                          <BdTooltip text={description}>{label}</BdTooltip>
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </React.Fragment>
              ))
            : '—'}
        </List.Item>

        <List.Item>
          <Typography variant="body2" color="deepSea50">
            <BdTooltip text="The time between exposure to the agent and the development of symptoms.">
              Incubation Period
            </BdTooltip>
          </Typography>
          <Typography variant="body2" color="stone90">
            {incubationPeriod || '—'}
          </Typography>
        </List.Item>

        <List.Item>
          <Typography variant="body2" color="deepSea50">
            <BdTooltip
              text={
                symptomaticPeriod
                  ? 'The duration between the appearance of the first symptoms and fully recovering from the disease without medical intervention.'
                  : 'Natural symptomatic period is not well established.'
              }
            >
              Symptomatic Period
            </BdTooltip>
          </Typography>
          <Typography variant="body2" color="stone90">
            {symptomaticPeriod || NoSymptomaticPeriodText}
          </Typography>
        </List.Item>

        <List.Item>
          <Typography variant="body2" color="deepSea50">
            <BdTooltip text="The ways in which disease transmission can be minimized.">
              Prevention Measure
            </BdTooltip>
          </Typography>
          <Typography variant="body2" color="stone90">
            {preventionMeasure || '—'}
          </Typography>
        </List.Item>

        <List.Item>
          <Typography variant="body2" color="deepSea50">
            <BdTooltip customPopup={popupBiosecurity}>Biosecurity Risk</BdTooltip>
          </Typography>
          <Typography variant="body2" color="stone90">
            {biosecurityRisk || '—'}
          </Typography>
        </List.Item>
      </List>
    </div>
  );
};

export default DiseaseAttributes;
